// GS1 Application Identifiers definitions
// Based on GS1 General Specifications

export interface ApplicationIdentifierDefinition {
  ai: string
  title: string
  shortName: string
  format: 'numeric' | 'alphanumeric' | 'date'
  minLength: number
  maxLength: number
  fixedLength: boolean
  category: 'identification' | 'traceability' | 'healthcare' | 'internal'
  description: string
}

// Application Identifiers supported in the initial implementation
export const APPLICATION_IDENTIFIERS: ApplicationIdentifierDefinition[] = [
  // Identification & Traceability
  {
    ai: '00',
    title: 'SSCC',
    shortName: 'SSCC',
    format: 'numeric',
    minLength: 18,
    maxLength: 18,
    fixedLength: true,
    category: 'identification',
    description: 'Serial Shipping Container Code'
  },
  {
    ai: '01',
    title: 'GTIN',
    shortName: 'GTIN',
    format: 'numeric',
    minLength: 14,
    maxLength: 14,
    fixedLength: true,
    category: 'identification',
    description: 'Global Trade Item Number'
  },
  {
    ai: '02',
    title: 'GTIN Content',
    shortName: 'CONTENT',
    format: 'numeric',
    minLength: 14,
    maxLength: 14,
    fixedLength: true,
    category: 'identification',
    description: 'GTIN of Trade Items Contained in a Logistic Unit'
  },
  {
    ai: '10',
    title: 'Batch/Lot',
    shortName: 'BATCH/LOT',
    format: 'alphanumeric',
    minLength: 1,
    maxLength: 20,
    fixedLength: false,
    category: 'traceability',
    description: 'Batch or Lot Number'
  },
  {
    ai: '11',
    title: 'Prod Date',
    shortName: 'PROD DATE',
    format: 'date',
    minLength: 6,
    maxLength: 6,
    fixedLength: true,
    category: 'traceability',
    description: 'Production Date (YYMMDD)'
  },
  {
    ai: '17',
    title: 'Exp Date',
    shortName: 'USE BY',
    format: 'date',
    minLength: 6,
    maxLength: 6,
    fixedLength: true,
    category: 'traceability',
    description: 'Expiration Date (YYMMDD)'
  },
  {
    ai: '21',
    title: 'Serial',
    shortName: 'SERIAL',
    format: 'alphanumeric',
    minLength: 1,
    maxLength: 20,
    fixedLength: false,
    category: 'traceability',
    description: 'Serial Number'
  },
  {
    ai: '37',
    title: 'Quantity',
    shortName: 'COUNT',
    format: 'numeric',
    minLength: 1,
    maxLength: 8,
    fixedLength: false,
    category: 'traceability',
    description: 'Count of Trade Items'
  },

  // Healthcare / NHRN (National Healthcare Reimbursement Number)
  {
    ai: '710',
    title: 'NHRN PZN',
    shortName: 'NHRN PZN',
    format: 'alphanumeric',
    minLength: 1,
    maxLength: 20,
    fixedLength: false,
    category: 'healthcare',
    description: 'Germany - Pharmazentralnummer (PZN)'
  },
  {
    ai: '711',
    title: 'NHRN CIP',
    shortName: 'NHRN CIP',
    format: 'alphanumeric',
    minLength: 1,
    maxLength: 20,
    fixedLength: false,
    category: 'healthcare',
    description: 'France - Code Identifiant de Présentation (CIP)'
  },
  {
    ai: '712',
    title: 'NHRN CN',
    shortName: 'NHRN CN',
    format: 'alphanumeric',
    minLength: 1,
    maxLength: 20,
    fixedLength: false,
    category: 'healthcare',
    description: 'Spain - Código Nacional (CN)'
  },
  {
    ai: '713',
    title: 'NHRN DRN',
    shortName: 'NHRN DRN',
    format: 'alphanumeric',
    minLength: 1,
    maxLength: 20,
    fixedLength: false,
    category: 'healthcare',
    description: 'Brazil - Drug Registration Number (ANVISA)'
  },
  {
    ai: '714',
    title: 'NHRN AIM',
    shortName: 'NHRN AIM',
    format: 'alphanumeric',
    minLength: 1,
    maxLength: 20,
    fixedLength: false,
    category: 'healthcare',
    description: 'Generic NHRN'
  },
  {
    ai: '715',
    title: 'NHRN NDC',
    shortName: 'NHRN NDC',
    format: 'alphanumeric',
    minLength: 1,
    maxLength: 20,
    fixedLength: false,
    category: 'healthcare',
    description: 'USA - National Drug Code (NDC)'
  },

  // Internal Use (90-99)
  {
    ai: '90',
    title: 'Internal',
    shortName: 'INTERNAL',
    format: 'alphanumeric',
    minLength: 1,
    maxLength: 30,
    fixedLength: false,
    category: 'internal',
    description: 'Information mutually agreed between trading partners'
  },
  {
    ai: '91',
    title: 'Internal',
    shortName: 'INTERNAL',
    format: 'alphanumeric',
    minLength: 1,
    maxLength: 90,
    fixedLength: false,
    category: 'internal',
    description: 'Company internal information'
  },
  {
    ai: '92',
    title: 'Internal',
    shortName: 'INTERNAL',
    format: 'alphanumeric',
    minLength: 1,
    maxLength: 90,
    fixedLength: false,
    category: 'internal',
    description: 'Company internal information'
  },
  {
    ai: '93',
    title: 'Internal',
    shortName: 'INTERNAL',
    format: 'alphanumeric',
    minLength: 1,
    maxLength: 90,
    fixedLength: false,
    category: 'internal',
    description: 'Company internal information'
  },
  {
    ai: '94',
    title: 'Internal',
    shortName: 'INTERNAL',
    format: 'alphanumeric',
    minLength: 1,
    maxLength: 90,
    fixedLength: false,
    category: 'internal',
    description: 'Company internal information'
  },
  {
    ai: '95',
    title: 'Internal',
    shortName: 'INTERNAL',
    format: 'alphanumeric',
    minLength: 1,
    maxLength: 90,
    fixedLength: false,
    category: 'internal',
    description: 'Company internal information'
  },
  {
    ai: '96',
    title: 'Internal',
    shortName: 'INTERNAL',
    format: 'alphanumeric',
    minLength: 1,
    maxLength: 90,
    fixedLength: false,
    category: 'internal',
    description: 'Company internal information'
  },
  {
    ai: '97',
    title: 'Internal',
    shortName: 'INTERNAL',
    format: 'alphanumeric',
    minLength: 1,
    maxLength: 90,
    fixedLength: false,
    category: 'internal',
    description: 'Company internal information'
  },
  {
    ai: '98',
    title: 'Internal',
    shortName: 'INTERNAL',
    format: 'alphanumeric',
    minLength: 1,
    maxLength: 90,
    fixedLength: false,
    category: 'internal',
    description: 'Company internal information'
  },
  {
    ai: '99',
    title: 'Internal',
    shortName: 'INTERNAL',
    format: 'alphanumeric',
    minLength: 1,
    maxLength: 90,
    fixedLength: false,
    category: 'internal',
    description: 'Company internal information'
  },
]

// Get AI definition by code
export function getAIDefinition(ai: string): ApplicationIdentifierDefinition | undefined {
  return APPLICATION_IDENTIFIERS.find(def => def.ai === ai)
}

// Get AIs by category
export function getAIsByCategory(category: ApplicationIdentifierDefinition['category']): ApplicationIdentifierDefinition[] {
  return APPLICATION_IDENTIFIERS.filter(def => def.category === category)
}

// Calculate GS1 check digit (Modulo 10)
export function calculateGTINCheckDigit(gtin: string): number {
  // Work with 13 digits (exclude check digit position)
  const digits = gtin.slice(0, 13).padStart(13, '0')
  let sum = 0
  for (let i = 0; i < 13; i++) {
    const digit = parseInt(digits[i])
    // Alternating multipliers: 1, 3, 1, 3... (from left to right for GTIN-14)
    sum += digit * (i % 2 === 0 ? 1 : 3)
  }
  return (10 - (sum % 10)) % 10
}

// Validate GTIN check digit
export function validateGTINCheckDigit(gtin: string): { valid: boolean; expected?: number; actual?: number } {
  if (gtin.length !== 14 || !/^\d+$/.test(gtin)) {
    return { valid: false }
  }
  const expected = calculateGTINCheckDigit(gtin)
  const actual = parseInt(gtin[13])
  return { valid: expected === actual, expected, actual }
}

// Validate AI value
export function validateAIValue(ai: string, value: string): { valid: boolean; error?: string; warning?: string } {
  const definition = getAIDefinition(ai)
  if (!definition) {
    return { valid: false, error: `Unknown AI: ${ai}` }
  }

  if (value.length < definition.minLength) {
    return { valid: false, error: `Value too short. Minimum ${definition.minLength} characters.` }
  }

  if (value.length > definition.maxLength) {
    return { valid: false, error: `Value too long. Maximum ${definition.maxLength} characters.` }
  }

  if (definition.format === 'numeric' && !/^\d+$/.test(value)) {
    return { valid: false, error: 'Value must be numeric.' }
  }

  if (definition.format === 'date') {
    if (!/^\d{6}$/.test(value)) {
      return { valid: false, error: 'Date must be in YYMMDD format.' }
    }
    const month = parseInt(value.substring(2, 4))
    const day = parseInt(value.substring(4, 6))
    if (month < 1 || month > 12) {
      return { valid: false, error: 'Invalid month in date.' }
    }
    if (day < 1 || day > 31) {
      return { valid: false, error: 'Invalid day in date.' }
    }
  }

  // Check GTIN check digit for AI 01 and 02
  if ((ai === '01' || ai === '02') && value.length === 14) {
    const checkDigitResult = validateGTINCheckDigit(value)
    if (!checkDigitResult.valid && checkDigitResult.expected !== undefined) {
      return {
        valid: true,
        warning: `Invalid check digit. Expected: ${checkDigitResult.expected}, got: ${checkDigitResult.actual}`
      }
    }
  }

  return { valid: true }
}

// Format AI for display (with parentheses)
export function formatAIForDisplay(ai: string, value: string): string {
  return `(${ai})${value}`
}
